import { usePageTitle } from '../hooks/usePageTitle';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, MailCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { PENDING_INVITE_TOKEN_KEY } from '../utils/constants';

interface InviteInfo {
  org_name: string;
  domain: string;
  expires_at: string;
}

const buttonStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-6)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)', textDecoration: 'none' };
const secondaryButtonStyle: React.CSSProperties = { ...buttonStyle, background: 'transparent', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-default)' };

function storePendingToken(token: string | null) {
  try {
    if (token) sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
    else sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
  } catch {
    // sessionStorage indisponível (ex: navegação privada restrita) — o fluxo segue sem o atalho
  }
}

function errorMessage(err: unknown, fallback: string) {
  return err instanceof Error && err.message ? err.message : fallback;
}

function JoinCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)', background: 'var(--color-bg-root)', color: 'var(--color-text-primary)' }}>
      <div style={{ maxWidth: '460px', width: '100%', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-8)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', textAlign: 'center', alignItems: 'center' }}>
        <img src="/logo.png" alt="Lictor" style={{ width: '48px', height: '48px' }} />
        {icon}
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', margin: 0 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** /join/:token — página aberta a partir do link de convite. */
export function JoinOrganization() {
  usePageTitle('Convite');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user, logout } = useAuth();

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    apiClient.get<InviteInfo>(`/orgs/invite-link/${encodeURIComponent(token)}`, { skipAuth: true })
      .then(setInfo)
      .catch((err) => {
        storePendingToken(null);
        setLoadError(errorMessage(err, 'Link de convite inválido.'));
      });
  }, [token]);

  useEffect(() => {
    if (!info || !token || authLoading) return;
    if (!isAuthenticated) {
      // Guarda o convite e manda para o cadastro; depois do cadastro/login a pessoa volta para cá
      storePendingToken(token);
      navigate(`/register?domain=${encodeURIComponent(info.domain)}`, { replace: true });
    } else {
      storePendingToken(null);
    }
  }, [info, token, authLoading, isAuthenticated, navigate]);

  const handleJoin = async () => {
    if (!token) return;
    setIsJoining(true);
    setJoinError(null);
    try {
      const res = await apiClient.post<{ email: string }>(`/orgs/invite-link/${encodeURIComponent(token)}/join`);
      setSentTo(res.email);
    } catch (err) {
      setJoinError(errorMessage(err, 'Não foi possível entrar na organização.'));
    } finally {
      setIsJoining(false);
    }
  };

  const handleSwitchAccount = async () => {
    await logout();
    // Sem login, o efeito acima guarda o convite e leva para o cadastro
  };

  if (loadError) {
    return (
      <JoinCard icon={<AlertTriangle size={32} style={{ color: 'var(--color-error)' }} />} title="Convite indisponível">
        <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>{loadError}</p>
        <Link to={isAuthenticated ? '/dashboard' : '/'} style={secondaryButtonStyle}>Voltar</Link>
      </JoinCard>
    );
  }

  if (!info || authLoading || !isAuthenticated) {
    return (
      <JoinCard icon={<Building2 size={32} style={{ color: 'var(--color-primary-400)' }} />} title="Carregando convite...">
        <span />
      </JoinCard>
    );
  }

  if (sentTo) {
    return (
      <JoinCard icon={<MailCheck size={32} style={{ color: 'var(--color-primary-400)' }} />} title="Confirme pelo seu e-mail">
        <p style={{ color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.6 }}>
          Enviamos um e-mail para <strong>{sentTo}</strong>. Abra-o e clique em <strong>Confirmar Entrada</strong> para concluir sua entrada em <strong>{info.org_name}</strong>. O link do e-mail vale por 1 hora.
        </p>
        <Link to="/dashboard" style={secondaryButtonStyle}>Ir para o painel</Link>
      </JoinCard>
    );
  }

  const emailMatches = user?.email?.split('@').pop()?.toLowerCase() === info.domain;

  return (
    <JoinCard icon={<Building2 size={32} style={{ color: 'var(--color-primary-400)' }} />} title={`Convite para ${info.org_name}`}>
      <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Este convite é restrito a e-mails <strong>@{info.domain}</strong>.
      </p>

      {emailMatches ? (
        <>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0, fontSize: 'var(--text-sm)' }}>
            Você está conectado como <strong>{user?.email}</strong>. Por segurança, vamos confirmar pelo seu e-mail antes de concluir.
          </p>
          {joinError && <p style={{ color: 'var(--color-error)', margin: 0, fontSize: 'var(--text-sm)' }}>{joinError}</p>}
          <button type="button" onClick={handleJoin} disabled={isJoining} style={{ ...buttonStyle, opacity: isJoining ? 0.6 : 1 }}>
            {isJoining ? 'Enviando...' : 'Entrar na Organização'}
          </button>
        </>
      ) : (
        <>
          <p style={{ color: 'var(--color-error)', margin: 0 }}>Seu e-mail não atende às regras deste convite.</p>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0, fontSize: 'var(--text-sm)' }}>
            Você está conectado como <strong>{user?.email}</strong>. Para usar este convite, entre com uma conta @{info.domain}.
          </p>
          <button type="button" onClick={handleSwitchAccount} style={secondaryButtonStyle}>Sair e usar outra conta</button>
        </>
      )}
    </JoinCard>
  );
}

/** /join/confirm?code=... — aberta pelo botão do e-mail de confirmação. */
export function JoinConfirm() {
  usePageTitle('Convite');
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const { isAuthenticated, isLoading: authLoading, loadUser } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const requested = useRef(false);

  useEffect(() => {
    // Espera saber se há sessão aberta (para recarregar a org ativa depois)
    if (authLoading) return;
    // O código é de uso único: garante uma só chamada (o React em dev executa efeitos 2x)
    if (requested.current) return;
    requested.current = true;

    if (!code) {
      setStatus('error');
      setMessage('Link de confirmação inválido.');
      return;
    }
    apiClient.post<{ message: string }>('/orgs/invite-link/confirm', { code }, { skipAuth: true })
      .then(async (res) => {
        setMessage(res.message);
        setStatus('success');
        // Se já estiver logado, recarrega a organização ativa (agora é a do convite)
        if (isAuthenticated) await loadUser();
      })
      .catch((err) => {
        setMessage(errorMessage(err, 'Não foi possível confirmar sua entrada.'));
        setStatus('error');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, authLoading]);

  if (status === 'loading') {
    return (
      <JoinCard icon={<MailCheck size={32} style={{ color: 'var(--color-primary-400)' }} />} title="Confirmando...">
        <span />
      </JoinCard>
    );
  }

  return (
    <JoinCard
      icon={status === 'success'
        ? <CheckCircle2 size={32} style={{ color: 'var(--color-primary-400)' }} />
        : <AlertTriangle size={32} style={{ color: 'var(--color-error)' }} />}
      title={status === 'success' ? 'Entrada confirmada' : 'Não foi possível confirmar'}
    >
      <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>{message}</p>
      <Link to={isAuthenticated ? '/dashboard' : '/login'} style={buttonStyle}>
        {isAuthenticated ? 'Ir para o painel' : 'Fazer login'}
      </Link>
    </JoinCard>
  );
}
